import { useState, useEffect, useCallback } from "react";
import { Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  ShoppingCart, Package, BarChart3, ArrowRight, CheckCircle, Clock, Truck,
  Plus, Trash2, ExternalLink, RefreshCw, AlertCircle, Store
} from "lucide-react";
import { useAuth } from "../context/AuthContext";

const STATUS_MAP = {
  pending: { label: "Pendiente", color: "bg-yellow-100 text-yellow-700" },
  processing: { label: "Procesando", color: "bg-blue-100 text-blue-700" },
  shipped: { label: "Enviado", color: "bg-purple-100 text-purple-700" },
  delivered: { label: "Entregado", color: "bg-green-100 text-green-700" },
};

const SHIPPING_STATUS_MAP = {
  pending_label: { label: "Sin guía", color: "text-yellow-600" },
  label_created: { label: "Guía creada", color: "text-blue-600" },
  in_transit: { label: "En tránsito", color: "text-purple-600" },
  delivered: { label: "Entregado", color: "text-green-600" },
};

const MultichannelPage = () => {
  const { user, authAxios } = useAuth();
  const [marketplaces, setMarketplaces] = useState([]);
  const [connections, setConnections] = useState([]);
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showConnect, setShowConnect] = useState(null);
  const [filterMarketplace, setFilterMarketplace] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");

  const fetchData = useCallback(async () => {
    try {
      const [mkRes, connRes, ordersRes, statsRes] = await Promise.all([
        authAxios.get("/marketplace/available"),
        authAxios.get("/marketplace/connections"),
        authAxios.get("/marketplace/orders"),
        authAxios.get("/marketplace/stats")
      ]);
      setMarketplaces(mkRes.data);
      setConnections(connRes.data);
      setOrders(ordersRes.data);
      setStats(statsRes.data);
    } catch (error) { /* error handled */; } finally { setLoading(false); }
  }, [authAxios]);

  useEffect(() => {
    if (user) fetchData();
  }, [user, fetchData]);

  const handleConnect = async (marketplaceId) => {
    try {
      await authAxios.post("/marketplace/connect", {
        marketplace_id: marketplaceId,
        credentials: { demo_mode: true }
      });
      toast.success("Marketplace conectado con datos de demostración");
      setShowConnect(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al conectar");
    }
  };

  const handleDisconnect = async (connectionId) => {
    if (!window.confirm("¿Desconectar este marketplace?")) return;
    try {
      await authAxios.delete(`/marketplace/disconnect/${connectionId}`);
      toast.success("Marketplace desconectado");
      fetchData();
    } catch (error) { toast.error("Error al desconectar"); }
  };

  const filteredOrders = orders.filter(o => {
    if (filterMarketplace !== "all" && o.marketplace_id !== filterMarketplace) return false;
    if (filterStatus !== "all" && o.status !== filterStatus) return false;
    return true;
  });

  const connectedIds = connections.map(c => c.marketplace_id);

  if (!user) return <Navigate to="/login" />;

  return (
    <div className="min-h-screen pt-24 pb-16 bg-[#FAF9F6]" data-testid="multichannel-page">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold text-[#5D4037]">Multicanal</h1>
            <p className="text-stone-600">Gestiona tus ventas de Mercado Libre y Amazon en un solo lugar</p>
          </div>
        </div>

        {/* Stats */}
        {!loading && stats && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
            {[
              { label: "Marketplaces", value: stats.connected_marketplaces, icon: Store, color: "#2E7D32" },
              { label: "Total Pedidos", value: stats.total_orders, icon: ShoppingCart, color: "#F57C00" },
              { label: "Pendientes", value: stats.pending_orders, icon: Clock, color: "#F59E0B" },
              { label: "Enviados", value: stats.shipped_orders, icon: Truck, color: "#8B5CF6" },
              { label: "Ingresos", value: `$${stats.total_revenue?.toLocaleString("es-MX")}`, icon: BarChart3, color: "#4CAF50" },
            ].map((s, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }} className="bento-card p-4" data-testid={`mc-stat-${i}`}>
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${s.color}15` }}>
                    <s.icon size={16} style={{ color: s.color }} />
                  </div>
                </div>
                <p className="text-2xl font-black text-[#5D4037]">{s.value}</p>
                <p className="text-xs text-stone-500">{s.label}</p>
              </motion.div>
            ))}
          </div>
        )}

        {/* Marketplace Connections */}
        <div className="mb-8">
          <h2 className="text-lg font-bold text-[#5D4037] mb-4">Marketplaces</h2>
          <div className="grid md:grid-cols-2 gap-4">
            {marketplaces.map((mk) => {
              const connected = connectedIds.includes(mk.id);
              const connection = connections.find(c => c.marketplace_id === mk.id);
              return (
                <div key={mk.id} className={`bento-card p-6 ${connected ? "ring-2 ring-[#4CAF50]" : ""}`} data-testid={`marketplace-${mk.id}`}>
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ backgroundColor: mk.logo_color }}>
                        <Store size={24} className="text-white" />
                      </div>
                      <div>
                        <h3 className="font-bold text-[#5D4037]">{mk.name}</h3>
                        <p className="text-sm text-stone-500">{mk.description}</p>
                      </div>
                    </div>
                    {connected ? (
                      <span className="flex items-center gap-1 text-sm text-green-600 font-medium"><CheckCircle size={16} /> Conectado</span>
                    ) : null}
                  </div>
                  <div className="flex flex-wrap gap-2 mb-4">
                    {mk.features?.slice(0, 3).map((f, i) => (
                      <span key={i} className="text-xs bg-stone-100 text-stone-600 px-2 py-1 rounded">{f}</span>
                    ))}
                  </div>
                  {connected ? (
                    <div className="flex items-center justify-between">
                      <div className="text-sm text-stone-500">
                        <span className="font-medium text-[#2E7D32]">{connection?.orders_synced || 0}</span> pedidos sincronizados
                      </div>
                      <button onClick={() => handleDisconnect(connection?.id)} className="text-sm text-red-500 hover:text-red-700 flex items-center gap-1">
                        <Trash2 size={14} /> Desconectar
                      </button>
                    </div>
                  ) : (
                    <button onClick={() => handleConnect(mk.id)} className="w-full btn-primary py-2 flex items-center justify-center gap-2 text-sm" data-testid={`connect-${mk.id}`}>
                      <Plus size={16} /> Conectar {mk.name}
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Orders */}
        <div>
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-4">
            <h2 className="text-lg font-bold text-[#5D4037]">Pedidos ({filteredOrders.length})</h2>
            <div className="flex gap-2">
              <select value={filterMarketplace} onChange={(e) => setFilterMarketplace(e.target.value)} className="h-10 px-3 rounded-lg border border-stone-200 text-sm outline-none bg-white" data-testid="filter-marketplace">
                <option value="all">Todos los marketplaces</option>
                <option value="mercadolibre">Mercado Libre</option>
                <option value="amazon">Amazon</option>
              </select>
              <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className="h-10 px-3 rounded-lg border border-stone-200 text-sm outline-none bg-white" data-testid="filter-status">
                <option value="all">Todos los estados</option>
                <option value="pending">Pendientes</option>
                <option value="processing">Procesando</option>
                <option value="shipped">Enviados</option>
                <option value="delivered">Entregados</option>
              </select>
            </div>
          </div>

          {loading ? (
            <div className="text-center py-12">Cargando...</div>
          ) : filteredOrders.length === 0 ? (
            <div className="bento-card p-12 text-center">
              <ShoppingCart size={48} className="text-stone-300 mx-auto mb-4" />
              <p className="text-stone-500">
                {connections.length === 0 ? "Conecta un marketplace para ver tus pedidos" : "No hay pedidos con estos filtros"}
              </p>
              {connections.length === 0 && (
                <p className="text-sm text-stone-400 mt-2">Conecta Mercado Libre o Amazon para sincronizar pedidos automáticamente</p>
              )}
            </div>
          ) : (
            <div className="space-y-3">
              {filteredOrders.map((order) => (
                <motion.div key={order.id} initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="bento-card p-4 flex items-center gap-4" data-testid={`order-${order.id}`}>
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: order.marketplace_id === "mercadolibre" ? "#FFE60020" : "#FF990020" }}>
                    <Store size={18} style={{ color: order.marketplace_id === "mercadolibre" ? "#333" : "#FF9900" }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="font-mono font-bold text-[#5D4037] text-sm">{order.order_number}</p>
                      <span className="text-xs text-stone-400">{order.marketplace_name}</span>
                    </div>
                    <p className="text-sm text-stone-600">{order.buyer_name} - {order.items?.[0]?.name} {order.items?.length > 1 ? `+${order.items.length - 1} más` : ""}</p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="font-bold text-[#2E7D32]">${order.total?.toLocaleString("es-MX")}</p>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${STATUS_MAP[order.status]?.color || "bg-stone-100 text-stone-600"}`}>
                      {STATUS_MAP[order.status]?.label || order.status}
                    </span>
                  </div>
                  <div className="flex-shrink-0">
                    <span className={`text-xs ${SHIPPING_STATUS_MAP[order.shipping_status]?.color || "text-stone-500"}`}>
                      {SHIPPING_STATUS_MAP[order.shipping_status]?.label || ""}
                    </span>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MultichannelPage;
